import { inject } from "@angular/core";
import { map, Observable, ObservableInput, ObservedValueOf, of, OperatorFunction, switchMap, take } from "rxjs";
import { FeatureFlag } from "../../../shared/models/enums/feature-flag";
import { LDFeatureManager } from "../../../shared/services/ld-feature-manager";

const CALLER = Symbol('Calling Method Name');

export abstract class NSwagMigrationService {
    protected abstract readonly className: string;
    protected abstract readonly featureFlag: FeatureFlag;
    protected readonly featureManager = inject(LDFeatureManager);

    constructor() {
        for (const name of this.getPropertyNames()) {
            if (typeof this[name] === 'function' && name !== 'constructor') {
                this[name] = this[name].bind(this.createProxy(name))
            }
        }
    }

    protected invoke<O extends ObservableInput<any>>(project: (isDisabled: boolean) => O): Observable<ObservedValueOf<O>> {
        const { featureManager, featureFlag } = this;
        const isDisabled$ = featureManager?.isDisabled?.(featureFlag) ?? of(true); // <== for bad unit tests.
        return isDisabled$.pipe(
            map(x => !(window['featureFlagOverrides']?.[featureFlag] ?? !x)), // <== For testing
            this.runInZone(this[CALLER]),
            switchMap(project),
            take(1));
    }

    private runInZone(caller: string): OperatorFunction<boolean, boolean> {
        return (source) => new Observable(observer => {
            return source.subscribe({
                next: (disabled: boolean) => {
                    const zone = Zone.current.fork({
                        name: 'NSwag Migration',
                        properties: {
                            data: {
                                flag: this.featureFlag,
                                enabled: !disabled,
                                service: this.className,
                                method: caller
                            }
                        }
                    });
                    return zone.run(() => observer.next(disabled));
                },
                error: (e: any) => observer.error(e),
                complete: () => observer.complete()
            });
        });
    }

    private createProxy(name: string): this {
        return new Proxy(this, { get: (target, prop, receiver) => prop === CALLER ? name : Reflect.get(target, prop, receiver) });
    }

    private *getPropertyNames(): Generator<string, void, undefined> {
        for (let proto = Object.getPrototypeOf(this); !!proto && proto !== NSwagMigrationService.prototype; proto = Object.getPrototypeOf(proto)) {
            for (const [name, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(proto))) {
                // Exclude properties with getter/setter
                if (!descriptor.get && !descriptor.set) {
                    yield name;
                }
            }
        }
    }
}
